import React, { useRef, useEffect } from "react";
import { getMapCalibration, worldToPixel } from "../../constants/mapCalibration";

const RENDER_SIZE = 700;

function colorForSlot(slot) {
  const hue = (slot * 47) % 360;
  return `hsl(${hue}, 80%, 55%)`;
}

export default function RadarMap({ mapName, players, liveSignals }) {
  const canvasRef = useRef(null);
  const calibration = getMapCalibration(mapName);

  useEffect(() => {
    if (!calibration || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, RENDER_SIZE, RENDER_SIZE);

    const bySlot = {};
    (players || []).forEach((p) => { bySlot[p.slot] = p; });

    // live-signal lines (suspect -> unseen enemy) drawn under the dots
    (liveSignals || []).forEach((sig) => {
      const s = bySlot[sig.suspectSlot];
      const e = bySlot[sig.enemySlot];
      if (!s || !e) return;
      const a = worldToPixel({ x: s.x, y: s.y }, calibration, RENDER_SIZE);
      const b = worldToPixel({ x: e.x, y: e.y }, calibration, RENDER_SIZE);
      ctx.save();
      ctx.strokeStyle = "rgba(255,60,60,0.9)";
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(a.px, a.py);
      ctx.lineTo(b.px, b.py);
      ctx.stroke();
      ctx.restore();
    });

    (players || []).forEach((p) => {
      const { px, py } = worldToPixel({ x: p.x, y: p.y }, calibration, RENDER_SIZE);
      const color = colorForSlot(p.slot);

      // view direction arrow (yaw in degrees; +Y world up => screen y uses -sin)
      const yawRad = (p.yaw * Math.PI) / 180;
      const len = 18;
      const ax = px + Math.cos(yawRad) * len;
      const ay = py - Math.sin(yawRad) * len;
      ctx.save();
      ctx.globalAlpha = p.alive ? 1 : 0.35;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(ax, ay);
      ctx.stroke();

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(px, py, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });
  }, [calibration, players, liveSignals]);

  if (!calibration) {
    return <div style={{ padding: 16 }}>No radar calibration for map "{mapName || "unknown"}".</div>;
  }

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div style={{ position: "relative", width: RENDER_SIZE, height: RENDER_SIZE }}>
        <img
          src={calibration.imageUrl}
          alt={mapName}
          width={RENDER_SIZE}
          height={RENDER_SIZE}
          style={{ position: "absolute", top: 0, left: 0, userSelect: "none" }}
        />
        <canvas
          ref={canvasRef}
          width={RENDER_SIZE}
          height={RENDER_SIZE}
          style={{ position: "absolute", top: 0, left: 0 }}
        />
      </div>
      <div style={{ minWidth: 180 }}>
        {(players || []).map((p) => (
          <div key={p.slot} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: p.alive ? 1 : 0.5 }}>
            <span style={{ width: 12, height: 12, background: colorForSlot(p.slot), display: "inline-block", borderRadius: 2 }} />
            <span>{p.name} {p.alive ? "" : "(dead)"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
